import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { safeHotelImageUrl } from '@bandido/shared/types';
import { client } from '@frontend/features/common/services';
import { CONFIG } from '@frontend/config';
import { catalogAdminKey } from './queries';
import { DetailConnection } from './detail';
import { EditorialSnapshot, Publication } from './editorial';
import { discoveryPending } from './discovery';
import { composeSourceContent } from './sourceContent';
interface Preview {
  supplier_hotel_id: number;
  source_revision: string;
  source_observed_at: string;
  snapshot: EditorialSnapshot;
}
interface Refresh {
  job_id: string;
  supplier_hotel_id: number;
  status: string;
  hotels_received: number;
  updated_at: string;
}
interface StoredRefresh {
  request?: { request_id: string; language?: string };
  job_id?: string;
}
export function SourceCuration({
  hotelId,
  source,
  onClose,
}: {
  hotelId: string;
  source: DetailConnection;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const label = (k: string, o?: Record<string, unknown>) => t(`hotelSourceCuration.${k}`, o);
  const cache = useQueryClient();
  const path = `hotel-catalog-admin/hotels/${encodeURIComponent(hotelId)}`;
  const storageKey = `catalog-source-refresh:${CONFIG.AGENCY_ID}:${hotelId}:${source.supplier_hotel_id}`;
  const [stored, setStored] = useState<StoredRefresh>(() => {
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
      if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
      if (typeof value.job_id === 'string' && value.job_id) return { job_id: value.job_id };
      if (value.request && typeof value.request.request_id === 'string') return { request: value.request };
      return {};
    } catch {
      return {};
    }
  });
  const [language, setLanguage] = useState('ENG'),
    [reason, setReason] = useState(''),
    [confirmed, setConfirmed] = useState(false),
    [storageError, setStorageError] = useState(false);
  const inFlight = useRef(false),
    seenCompletion = useRef('');
  const active = source.link_status === 'active' && source.account_status === 'active';
  const saveStored = (value: StoredRefresh) => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(value));
      setStored(value);
      setStorageError(false);
      return true;
    } catch {
      setStorageError(true);
      return false;
    }
  };
  const publication = useQuery({
    queryKey: [...catalogAdminKey, 'editorial', hotelId, 'publication'],
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => (await client.get<{ data: Publication }>(`${path}/publication`)).data.data,
  });
  const missing = publication.isError && (publication.error as any)?.response?.status === 404;
  const preview = useQuery({
    queryKey: [...catalogAdminKey, 'source-preview', hotelId, source.supplier_hotel_id],
    enabled: active,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      (await client.post<{ data: Preview }>(`${path}/source-preview`, { supplier_hotel_id: source.supplier_hotel_id }))
        .data.data,
  });
  const job = useQuery({
    queryKey: [...catalogAdminKey, 'source-refresh', stored.job_id],
    enabled: !!stored.job_id,
    retry: false,
    queryFn: async () => {
      const r = (
        await client.get<{ data: Refresh }>(
          `hotel-catalog-admin/source-refreshes/${encodeURIComponent(stored.job_id!)}`,
        )
      ).data.data;
      if (r.supplier_hotel_id !== source.supplier_hotel_id) throw new Error('source');
      return r;
    },
    refetchInterval: (data) => (discoveryPending(data?.status as any) ? 5000 : false),
    refetchIntervalInBackground: false,
  });
  const invalidate = () => {
    ['detail', 'connections', 'inventory', 'editorial', 'preparation-sources'].forEach((k) =>
      cache.invalidateQueries([...catalogAdminKey, k]),);
  };
  useEffect(() => {
    if (job.data?.status === 'completed' && seenCompletion.current !== job.data.job_id) {
      seenCompletion.current = job.data.job_id;
      preview.refetch();
      invalidate();
      setConfirmed(false);
    }
  }, [job.data?.status, job.data?.job_id]);
  const refresh = useMutation({
    retry: false,
    mutationFn: async (body: NonNullable<StoredRefresh['request']>) =>
      (await client.post<{ data: Refresh }>(`${path}/connections/${source.supplier_hotel_id}/refresh`, body)).data.data,
    onSuccess: (data) => {
      saveStored({ job_id: data.job_id });
      setConfirmed(false);
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });
  const startRefresh = () => {
    if (inFlight.current) return;
    const body = stored.request || {
      request_id: crypto.randomUUID(),
      ...(source.connector === 'hotelbeds' ? { language } : {}),
    };
    if (!saveStored({ request: body })) return;
    inFlight.current = true;
    publish.reset();
    setConfirmed(false);
    refresh.mutate(body);
  };
  const publish = useMutation({
    retry: false,
    mutationFn: async () => {
      if (
        !preview.data ||
        preview.isFetching ||
        preview.isError ||
        publication.isFetching ||
        (!missing && publication.isError) ||
        !confirmed ||
        !reason.trim()
      ) throw new Error('review');
      return client.post(`${path}/source-publication`, {
        supplier_hotel_id: source.supplier_hotel_id,
        source_revision: preview.data.source_revision,
        expected_version: missing ? 0 : publication.data!.version,
        reason: reason.trim(),
        confirm_global_publication: true,
      });
    },
    onSuccess: () => {
      invalidate();
      setConfirmed(false);
      setReason('');
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });
  const busy = refresh.isLoading || publish.isLoading;
  const pending = !!stored.request || (!!stored.job_id && (!job.data || discoveryPending(job.data.status as any)));
  const ready =
    active &&
    !!preview.data &&
    !preview.isError &&
    !preview.isFetching &&
    !publication.isFetching &&
    (missing || (!!publication.data && !publication.isError)) &&
    !pending &&
    !publish.isError;
  useEffect(() => setConfirmed(false), [preview.data?.source_revision, publication.data?.version]);
  const proposed = preview.data
    ? composeSourceContent(preview.data.snapshot, missing ? {} : publication.data?.overrides || {})
    : null;
  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <DialogTitle>
        {label('title')} · {source.account_name}
      </DialogTitle>
      <DialogContent sx={{ '& input,& textarea,& .MuiSelect-select': { fontSize: '16px !important' } }}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography>
            {label('sourceCode')}: {source.hotel_code}
          </Typography>
          <Alert severity="info">{label('separation')}</Alert>
          {!active && <Alert severity="warning">{label('disabled')}</Alert>}
          <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
            {source.connector === 'hotelbeds' && (
              <TextField
                select
                label={label('language')}
                sx={{ minWidth: 180 }}
                value={language}
                disabled={busy || pending}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <MenuItem value="ENG">{label('english')}</MenuItem>
                <MenuItem value="CAS">{label('spanish')}</MenuItem>
                <MenuItem value="POR">{label('portuguese')}</MenuItem>
              </TextField>
            )}
            <Button variant="outlined" disabled={!active || busy || pending} onClick={startRefresh}>
              {label('refresh')}
            </Button>
            {stored.request && (
              <Button disabled={busy} onClick={startRefresh}>
                {label('recover')}
              </Button>
            )}
            {stored.request && [400, 404, 409, 422].includes((refresh.error as any)?.response?.status) && (
              <Button
                disabled={busy}
                onClick={() => {
                  if (saveStored({})) refresh.reset();
                }}
              >
                {label('correct')}
              </Button>
            )}
          </Stack>
          {storageError && <Alert severity="error">{label('storageError')}</Alert>}
          {stored.request && <Alert severity="info">{label('uncertain')}</Alert>}
          {refresh.isError && <Alert severity="error">{label('refreshError')}</Alert>}
          {job.isError && (
            <Alert severity="warning" action={<Button onClick={() => job.refetch()}>{label('reload')}</Button>}>
              {label('refreshStatusError')}
            </Alert>
          )}
          {job.isError && (job.error as any)?.response?.status === 404 && (
            <Button disabled={busy} onClick={() => saveStored({})}>
              {label('forgetUnavailable')}
            </Button>
          )}
          {job.data && (
            <Stack spacing={1}>
              <Chip sx={{ alignSelf: 'flex-start' }} label={t(`hotelDiscovery.status.${job.data.status}`)} />
              {discoveryPending(job.data.status as any) && <LinearProgress />}
              {job.data.status === 'completed' && (
                <Alert severity={job.data.hotels_received ? 'success' : 'warning'}>
                  {label(job.data.hotels_received ? 'refreshed' : 'notReturned')}
                </Alert>
              )}
              {job.data.status === 'terminal_failed' && <Alert severity="warning">{label('refreshFailed')}</Alert>}
            </Stack>
          )}
          <Divider />
          <Typography variant="h6">{label('review')}</Typography>
          <Typography color="text.secondary">{label('preserve')}</Typography>
          {(preview.isFetching || publication.isFetching) && <LinearProgress />}
          {(preview.isError || (publication.isError && !missing)) && (
            <Alert severity="error">{label('loadError')}</Alert>
          )}
          <Button
            disabled={busy || pending}
            onClick={() => {
              setConfirmed(false);
              preview.refetch();
              publication.refetch();
              publish.reset();
            }}
          >
            {label('reload')}
          </Button>
          {ready && proposed && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box component="section" aria-label={label('current')}>
                <Typography variant="subtitle1">{label('current')}</Typography>
                {!missing && publication.data?.published === false && <Chip label={label('withdrawn')} />}{' '}
                {missing ? (
                  <Typography>{label('noPublication')}</Typography>
                ) : (
                  <ContentPreview
                    content={composeSourceContent(publication.data!.snapshot, publication.data!.overrides)}
                  />
                )}
              </Box>
              <Box component="section" aria-label={label('proposed')}>
                <Typography variant="subtitle1">{label('proposed')}</Typography>
                <ContentPreview content={proposed} />
              </Box>
            </Box>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                disabled={!ready || busy}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
            }
            label={label('globalConfirmation')}
          />
          <TextField
            multiline
            minRows={2}
            label={label('reason')}
            value={reason}
            disabled={busy}
            inputProps={{ maxLength: 512 }}
            onChange={(e) => setReason(e.target.value)}
          />
          {publish.isError && (
            <Alert severity="error">
              {label((publish.error as any)?.response?.status === 409 ? 'conflict' : 'publishError')}
            </Alert>
          )}
          {publish.isSuccess && <Alert severity="success">{label('published')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {label('close')}
        </Button>
        <Button
          variant="contained"
          disabled={!ready || busy || !confirmed || !reason.trim()}
          onClick={() => {
            if (!inFlight.current) {
              inFlight.current = true;
              publish.mutate();
            }
          }}
        >
          {label('publish')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
function ContentPreview({ content }: { content: EditorialSnapshot }) {
  const { t } = useTranslation();
  const missing = t('hotelDetail.missing');
  return (
    <Stack spacing={1} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, overflowWrap: 'anywhere' }}>
      <Typography fontWeight={600}>{content.display_name || missing}</Typography>
      <Typography>{content.category_code || missing}</Typography>
      <Typography>
        {[content.location?.address, content.location?.city, content.location?.country_code]
          .filter(Boolean)
          .join(', ') || missing}
      </Typography>
      <Typography>
        {t('hotelSourceCuration.latitude')}: {content.location?.latitude ?? missing}
      </Typography>
      <Typography>
        {t('hotelSourceCuration.longitude')}: {content.location?.longitude ?? missing}
      </Typography>
      {content.descriptions?.map((d, i) => (
        <Box key={i}>
          <Typography variant="caption">
            {d.language.toUpperCase()} · {d.kind}
          </Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>{d.text}</Typography>
        </Box>
      ))}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 1 }}>
        {content.images
          ?.filter((i) => safeHotelImageUrl(i.url))
          .map((img, i) => (
            <Box
              component="img"
              key={img.url}
              src={img.url}
              alt={`${content.display_name || ''} ${i + 1}`}
              loading="lazy"
              referrerPolicy="no-referrer"
              sx={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 1 }}
            />
          ))}
      </Box>
    </Stack>
  );
}
